import {
  ActivityDateArgs,
  GetAccountActivityArgs,
} from "../../services/holdings";
import { addDays, localToday, toIsoDate } from "../../utils/dates";
import { DateFormat, formatDate } from "../../utils";
import { ACTIVITY_PAGE_SIZE } from "./constants";
import {
  ActivityDateFilter,
  ActivityDateRange,
  ActivityRequest,
} from "./types";

export const DEFAULT_ACTIVITY_REQUEST: ActivityRequest = {
  search: "",
  date: { kind: "currentYear" },
  types: [],
  order: "DESC",
  holdingIds: [],
  accountLevel: false,
  page: null,
};

export const activityDateRangeOf = (
  date: Exclude<ActivityDateFilter, { kind: "allTime" }>,
  now: Date = new Date(),
): ActivityDateRange => {
  const today = localToday(now);
  switch (date.kind) {
    case "lastDays":
      // Inclusive bounds, so "last 7 days" is today and the six before it.
      return {
        fromDate: toIsoDate(addDays(today, -(date.days - 1))),
        toDate: toIsoDate(today),
      };
    case "currentYear":
      return {
        fromDate: toIsoDate(new Date(today.getFullYear(), 0, 1)),
        toDate: toIsoDate(today),
      };
    case "year":
      return { fromDate: `${date.year}-01-01`, toDate: `${date.year}-12-31` };
  }
};

// Ledger writes refuse dates before the account's start, so its activity years bound every entry.
export const activityDateCoversYears = (
  date: ActivityDateFilter,
  years: number[],
  now: Date = new Date(),
): boolean => {
  if (date.kind === "allTime") {
    return true;
  }
  if (years.length === 0) {
    return false;
  }
  const { fromDate, toDate } = activityDateRangeOf(date, now);
  const firstDay = `${Math.min(...years)}-01-01`;
  return fromDate <= firstDay && toDate >= toIsoDate(localToday(now));
};

export const activityDateRangeText = (
  date: ActivityDateFilter,
  now: Date = new Date(),
): string => {
  if (date.kind === "allTime") {
    return "Showing all activity";
  }
  const { fromDate, toDate } = activityDateRangeOf(date, now);
  const today = toIsoDate(localToday(now));
  // Display only: a year request still sends `year=`, but the line must not promise days that haven't happened.
  const shownTo = toDate > today ? today : toDate;
  return `Showing ${formatDate(fromDate, DateFormat.DMY_SPACE)} – ${formatDate(shownTo, DateFormat.DMY_SPACE)}`;
};

const toDateArgs = (date: ActivityDateFilter, now: Date): ActivityDateArgs => {
  switch (date.kind) {
    case "allTime":
      return {};
    case "year":
      return { year: date.year };
    default:
      return activityDateRangeOf(date, now);
  }
};

export const toActivityQueryArgs = (
  accountId: number,
  request: ActivityRequest,
  now: Date = new Date(),
): GetAccountActivityArgs => {
  const search = request.search.trim();
  const filters = {
    accountId,
    limit: ACTIVITY_PAGE_SIZE,
    ...(request.order === DEFAULT_ACTIVITY_REQUEST.order
      ? {}
      : { order: request.order }),
    ...(request.types.length > 0 ? { type: request.types } : {}),
    ...(search ? { searchBy: search } : {}),
    ...(request.holdingIds.length > 0 ? { holdingId: request.holdingIds } : {}),
    ...(request.accountLevel ? { accountLevel: true as const } : {}),
  };
  const dated = { ...filters, ...toDateArgs(request.date, now) };
  return request.page === null ? dated : { ...dated, page: request.page };
};
